import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Cable, ClipboardCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import CtaBand from "@/components/CtaBand";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const ETAPES = [
  { num: "01", title: "Préparation et repérage", text: "Vérification de l'adressage, du cheminement prévu, des conditions d'accès et des autorisations nécessaires avant l'intervention." },
  { num: "02", title: "Acheminement de la fibre", text: "Pose en aérien sur façade, en souterrain dans les fourreaux existants ou en parties communes, dans le respect des règles de l'art." },
  { num: "03", title: "Soudure et prise terminale", text: "Soudure au point de branchement, pose et repérage de la prise terminale optique (PTO) chez l'abonné ou dans les locaux professionnels." },
  { num: "04", title: "Mesures et vérifications", text: "Contrôles optiques de la liaison et correction immédiate de tout écart constaté avant clôture." },
  { num: "05", title: "Compte rendu documenté", text: "Photos, mesures et repérage transmis au donneur d'ordre pour validation et traçabilité." },
];

const FtthD3 = () => (
  <>
    <Seo
      title="Raccordement FTTH D3 : installation jusqu'à la prise | ATLAS THD"
      description="Raccordement FTTH D3 par ATLAS THD : acheminement aérien ou souterrain, soudure, pose de la PTO, mesures optiques et compte rendu documenté."
      breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Raccordement FTTH D3" }]}
    />

    <section className="border-b border-line bg-cloud" data-testid="ftthd3-hero">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Raccordement FTTH D3" }]} />
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-terrain">
          Le Déploiement — FTTH
        </motion.p>
        <motion.h1 {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl">
          Raccordement FTTH D3 : la dernière portion jusqu'à l'abonné
        </motion.h1>
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-6 max-w-3xl text-lg leading-relaxed text-steel">
          Le segment D3 relie le point de branchement à la prise terminale
          optique, chez le particulier comme dans les locaux professionnels.
          C'est la partie la plus exposée aux contraintes terrain — accès,
          façades, parties communes — et la plus déterminante pour la qualité
          perçue. ATLAS THD la réalise avec des techniciens équipés et des
          comptes rendus documentés.
        </motion.p>
        <motion.div {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.45 }} className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to="/contact" data-testid="ftthd3-devis-button" className="group flex items-center justify-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white">
            Parlez-nous de votre besoin
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a href="tel:+33605765066" data-testid="ftthd3-call-button" className="flex items-center justify-center gap-2 border border-midnight px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white">
            <Phone className="h-4 w-4" />
            06 05 76 50 66
          </a>
        </motion.div>
      </div>
    </section>

    <section className="bg-white py-20" data-testid="ftthd3-etapes">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">Le déroulé</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            Cinq étapes, du repérage au compte rendu
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-3">
          {ETAPES.map((s, i) => (
            <motion.div key={s.num} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-white p-8" data-testid={`ftthd3-etape-${i}`}>
              <span className="font-display text-3xl font-extrabold text-terrain">{s.num}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-midnight">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{s.text}</p>
            </motion.div>
          ))}
          <motion.div {...fadeUp} transition={{ duration: 0.4, delay: 0.4 }} className="bg-midnight p-8">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-ciel">Et après ?</p>
            <p className="mt-4 font-display text-2xl font-extrabold leading-snug tracking-tight text-white">
              Chaque raccordement peut faire l'objet d'un contrôle qualité indépendant.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Inspection, contrôle de conformité et remontées VQSE : notre
              activité audit sécurise la recette de vos raccordements.
            </p>
            <Link to="/qualite-controle" data-testid="ftthd3-link-qualite" className="mt-5 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-ciel transition-colors hover:text-white">
              Découvrir l'activité audit
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>

    <section className="bg-cloud py-16" data-testid="ftthd3-links">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-8 gap-y-3 px-4 sm:px-6 lg:px-8">
        <span className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-steel">
          <Cable className="h-4 w-4 text-terrain" />
          Voir aussi
        </span>
        <Link to="/terrain-fibre" data-testid="ftthd3-link-terrain" className="text-sm font-medium text-midnight underline decoration-terrain decoration-2 underline-offset-4 hover:text-terrain">
          Toutes nos prestations de déploiement fibre
        </Link>
        <Link to="/guides/etapes-raccordement-ftth-d3" data-testid="ftthd3-link-guide" className="text-sm font-medium text-midnight underline decoration-terrain decoration-2 underline-offset-4 hover:text-terrain">
          Guide : les étapes détaillées d'un raccordement FTTH D3
        </Link>
        <Link to="/faq" data-testid="ftthd3-link-faq" className="flex items-center gap-1.5 text-sm font-medium text-midnight underline decoration-terrain decoration-2 underline-offset-4 hover:text-terrain">
          <ClipboardCheck className="h-4 w-4 text-terrain" />
          Questions fréquentes
        </Link>
      </div>
    </section>

    <CtaBand title="Des raccordements FTTH D3 à réaliser ?" />
  </>
);

export default FtthD3;
